import { useCallback, useEffect } from 'react';

import { useSelectedLocation } from 'contexts';
import { lookupFleetUnit } from 'services/fleet-files';
import { FleetFile } from 'types';
import { EquipmentType } from 'types/damage';

import { useNewTransaction } from '.';

/*
 * Fleet unit lookup for the gate screens.
 *
 * What this replaces, and why each part of it mattered:
 *
 *   - `if (!response.ok) return null` collapsed 502, 503, 504 and 403 into the same value the
 *     caller got for a genuine 204 miss. The gate screen then said "No fleet file found" for a
 *     provider outage, and skipped the check entirely for a gate user who got a 403.
 *   - The cache was `Map<string, FleetFile.Unit>` keyed on the bare unit number. A miss recorded at
 *     one location survived a location switch, an equipment-type change, and any retry, for the
 *     whole session — and `null` was cached as though it were an answer.
 *
 * The hook now returns a discriminated union with no `null` member, so a caller cannot accidentally
 * treat a failure as an absent unit; the compiler makes them enumerate the cases.
 */

/* Composite key: a result is only valid for the inputs it was obtained for. */
const cacheKey = (unitNumber: string, locationId?: number, equipmentType?: EquipmentType): string =>
  `${locationId ?? 'none'}|${equipmentType ?? 'any'}|${unitNumber.trim().toUpperCase()}`;

const useFleetFileUnits = () => {
  const { selectedLocation } = useSelectedLocation();
  /*
   * `fleetFileInFlight` holds requests in flight, so concurrent callers for the same inputs share one
   * network request — across components, not only within one: the unit field and the account field
   * each hold their own instance of this hook, and ask for the same unit at the same moment.
   */
  const { fleetFileUnits: cache, fleetFileInFlight: inFlight } = useNewTransaction();

  /*
   * Drop cached results when the location changes. A negative result is specific to the location it
   * was obtained at, and serving it afterwards is how an operator ends up told a unit does not exist
   * at a terminal nobody ever asked about.
   */
  useEffect(() => {
    cache.current.clear();
    inFlight.current.clear();
  }, [selectedLocation?.id, selectedLocation?.fleetFileLookupEnabled, cache, inFlight]);

  /* Explicitly forget a result so Retry issues a real request instead of replaying the miss. */
  const invalidateFleetFileUnit = useCallback(
    (unitNumber: string, equipmentType?: EquipmentType) => {
      const key = cacheKey(unitNumber, selectedLocation?.id, equipmentType);
      inFlight.current.delete(key);
      cache.current.delete(key);
    },
    [selectedLocation?.id, cache, inFlight]
  );

  /* Discard everything — used when mappings may have changed under us. */
  const invalidateAllFleetFileUnits = useCallback(() => {
    inFlight.current.clear();
    cache.current.clear();
  }, [cache, inFlight]);

  const getFleetFileUnit = useCallback(
    async (unitNumber: string, equipmentType?: EquipmentType): Promise<FleetFile.FleetLookupOutcome> => {
      const locationId = selectedLocation?.id;
      if (!locationId) {
        return {
          kind: 'invalidRequest',
          error: { status: 400, errorCode: 'FLEET_INVALID_REQUEST', retryable: false, message: 'No location selected' }
        };
      }
      if (!unitNumber?.trim()) {
        return {
          kind: 'invalidRequest',
          error: {
            status: 400,
            errorCode: 'FLEET_INVALID_REQUEST',
            retryable: false,
            message: 'Unit number is required'
          }
        };
      }

      if (selectedLocation?.fleetFileLookupEnabled === false) return { kind: 'disabled' };

      const key = cacheKey(unitNumber, locationId, equipmentType);

      const cached = cache.current.get(key);
      if (cached) return cached;

      const pending = inFlight.current.get(key);
      if (pending) return pending;

      const request = lookupFleetUnit(locationId, unitNumber.trim(), equipmentType)
        .then(outcome => {
          /*
           * Only answers are cached. A failure is a transient condition, not a fact about the unit;
           * caching one would make Retry replay the outage instead of retrying it.
           */
          /* and only while this is still the current request: a retry or a location change supersedes it */
          const current = inFlight.current.get(key) === request;
          if (current && (outcome.kind === 'found' || outcome.kind === 'notFound')) {
            cache.current.set(key, outcome);
          }
          return outcome;
        })
        .finally(() => {
          /* shared now: a retry may have replaced this entry, and that newer request is not ours to drop */
          if (inFlight.current.get(key) === request) inFlight.current.delete(key);
        });

      inFlight.current.set(key, request);
      return request;
    },
    [selectedLocation?.id, selectedLocation?.fleetFileLookupEnabled, cache, inFlight]
  );

  return { getFleetFileUnit, invalidateFleetFileUnit, invalidateAllFleetFileUnits };
};

export default useFleetFileUnits;
export { cacheKey as __cacheKeyForTest };
